import { H2 } from "@/shared/components/typography";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { ManageBuildingAddressesSection } from "@/routes/processes/[processId]/qpm/manage-building/_components/manage-building-addresses-section";
import { ManageBuildingBuildingsSection } from "@/routes/processes/[processId]/qpm/manage-building/_components/manage-building-buildings-section";
import { useManageBuildingData } from "@/routes/processes/[processId]/qpm/manage-building/_components/use-manage-building-data";
import { ManageBuildingLoadingAndError } from "@/routes/processes/[processId]/qpm/manage-building/_components/manage-building-loading-and-error";
import { useCallback, useEffect, useMemo, useState } from "react";

export default function ManageBuildingRoute() {
  const [selectedServiceIndex, setSelectedServiceIndex] = useState(0);

  // First call without selectedServiceObjectId so we can load serviceObjectIds.
  const base = useManageBuildingData();

  useEffect(() => {
    if (!base.serviceObjectIds.length) {
      setSelectedServiceIndex(0);
      return;
    }
    if (selectedServiceIndex > base.serviceObjectIds.length - 1) {
      setSelectedServiceIndex(0);
    }
  }, [base.serviceObjectIds.length, selectedServiceIndex]);

  const selectedServiceObjectId = useMemo(() => {
    if (!base.serviceObjectIds.length) return undefined;
    return base.serviceObjectIds[selectedServiceIndex];
  }, [base.serviceObjectIds, selectedServiceIndex]);

  // Second call with selectedServiceObjectId for building list query scoping.
  const {
    processCustomerId,
    hasAddressList,
    isLoading,
    processError,
    addressError,
    buildingError,
    postalAddress,
    serviceObjectIds,
    buildingList,
    selectedBuilding,
    onBuildingSelect,
    onBuildingCreated,
    onDeleteBuilding,
  } = useManageBuildingData(selectedServiceObjectId);

  const onPrevServiceAddress = useCallback(() => {
    if (!serviceObjectIds.length) return;
    setSelectedServiceIndex((prev) =>
      prev <= 0 ? serviceObjectIds.length - 1 : prev - 1,
    );
  }, [serviceObjectIds.length]);

  const onNextServiceAddress = useCallback(() => {
    if (!serviceObjectIds.length) return;
    setSelectedServiceIndex((prev) =>
      prev >= serviceObjectIds.length - 1 ? 0 : prev + 1,
    );
  }, [serviceObjectIds.length]);

  const loadingOrError = (
    <ManageBuildingLoadingAndError
      isLoading={isLoading}
      processError={processError}
      addressError={addressError}
      buildingError={buildingError}
      hasProcess={!!processCustomerId}
      hasAddressList={hasAddressList}
    />
  );

  if (
    isLoading ||
    processError ||
    addressError ||
    buildingError ||
    !processCustomerId ||
    !hasAddressList
  ) {
    return loadingOrError;
  }

  return (
    <div className="space-y-4">
      <PersistenceContextProvider>
        <div className="flex items-end justify-between">
          <H2>{t("Gebäudeverwaltung")}</H2>
          <PersistenceStateUsingContext className="mr-2" />
        </div>

        <ManageBuildingAddressesSection
          postalAddress={postalAddress}
          serviceObjectIds={serviceObjectIds}
          selectedIndex={selectedServiceIndex}
          onPrev={onPrevServiceAddress}
          onNext={onNextServiceAddress}
        />
      </PersistenceContextProvider>

      <ManageBuildingBuildingsSection
        buildingList={buildingList}
        selectedBuilding={selectedBuilding}
        onBuildingSelect={onBuildingSelect}
        onBuildingCreated={onBuildingCreated}
        onDeleteBuilding={onDeleteBuilding}
        customerId={processCustomerId}
        serviceObjectId={selectedServiceObjectId ?? "SERVICE_OBJECT_ID_TODO"}
      />
    </div>
  );
}
